import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link, useParams } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import {
  ArrowLeft, ArrowUpRight, Building2, CheckCircle2, ChevronLeft, ChevronRight,
  Home, ImageOff, Layers3, Loader2, Maximize2, Sparkles, X,
} from 'lucide-react';
import { fetchSaranaPrasaranaBySlug } from '../utils/api';
import SEO from '../components/SEO';

const imageVariant = (src, width) => {
  const supportsResize = src?.includes('/api/public/media/')
    || /\/facilities\/[a-f0-9-]{36}\.webp(?:\?|$)/i.test(src);
  if (!supportsResize) return src;
  return `${src}${src.includes('?') ? '&' : '?'}w=${width}`;
};

const supportsResponsiveImage = (src) => src?.includes('/api/public/media/')
  || /\/facilities\/[a-f0-9-]{36}\.webp(?:\?|$)/i.test(src);

function FacilityImage({ item, className = '', alt, sizes = '(min-width: 1024px) 70vw, 100vw' }) {
  if (!item.imageUrl) {
    return (
      <div className={`grid place-items-center bg-gradient-to-br from-[#552080] to-[#18152c] text-white/60 ${className}`}>
        <ImageOff size={44} />
      </div>
    );
  }
  return (
    <img
      src={imageVariant(item.imageUrl, 1200)}
      srcSet={supportsResponsiveImage(item.imageUrl)
        ? `${imageVariant(item.imageUrl, 480)} 480w, ${imageVariant(item.imageUrl, 800)} 800w, ${imageVariant(item.imageUrl, 1200)} 1200w`
        : undefined}
      sizes={sizes}
      alt={alt || item.name || 'Fasilitas kampus'}
      className={`object-cover ${className}`}
    />
  );
}

function FacilityGallery({ item }) {
  const gallery = [
    ...(item.imageUrl ? [{ id: 'cover', imageUrl: item.imageUrl, isCover: true }] : []),
    ...(item.images || []).filter((image) => image.imageUrl),
  ];
  const [activeIndex, setActiveIndex] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const hasMultiple = gallery.length > 1;
  const activeImage = gallery[activeIndex];

  useEffect(() => {
    setActiveIndex(0);
    setLightboxOpen(false);
  }, [item.id]);

  useEffect(() => {
    if (!lightboxOpen) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKeyDown = (event) => {
      if (event.key === 'Escape') setLightboxOpen(false);
      if (event.key === 'ArrowLeft' && hasMultiple) {
        setActiveIndex((current) => (current - 1 + gallery.length) % gallery.length);
      }
      if (event.key === 'ArrowRight' && hasMultiple) {
        setActiveIndex((current) => (current + 1) % gallery.length);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [gallery.length, hasMultiple, lightboxOpen]);

  const previousImage = () => setActiveIndex((current) => (current - 1 + gallery.length) % gallery.length);
  const nextImage = () => setActiveIndex((current) => (current + 1) % gallery.length);

  return (
    <motion.section
      initial={{ opacity: 0, y: 18 }}
      animate={{ opacity: 1, y: 0 }}
      className="overflow-hidden rounded-3xl border border-white/70 bg-white p-2 shadow-xl shadow-purple-950/5 dark:border-white/10 dark:bg-[#1A182E] sm:p-3"
      aria-label={`Galeri ${item.name}`}
    >
      <div className="group relative overflow-hidden rounded-[1.25rem] bg-[#18152c]">
        {activeImage ? (
          <button
            type="button"
            onClick={() => setLightboxOpen(true)}
            className="block w-full cursor-zoom-in"
            aria-label="Buka gambar dalam layar penuh"
          >
            <FacilityImage
              key={activeImage.imageUrl}
              item={activeImage}
              alt={`${item.name} - gambar ${activeIndex + 1}`}
              className="aspect-[16/9] w-full transition duration-500 group-hover:scale-[1.015]"
            />
          </button>
        ) : (
          <FacilityImage item={item} className="aspect-[16/9] w-full" />
        )}
        {activeImage && (
          <div className="pointer-events-none absolute inset-x-0 bottom-0 flex items-end justify-between bg-gradient-to-t from-black/65 via-black/10 to-transparent px-4 pb-4 pt-14 text-white sm:px-5">
            <span className="rounded-full bg-black/35 px-3 py-1.5 text-xs font-semibold backdrop-blur-md">
              {activeIndex + 1} / {gallery.length}
            </span>
            <span className="flex items-center gap-1.5 rounded-full bg-black/35 px-3 py-1.5 text-xs font-semibold opacity-100 backdrop-blur-md transition sm:opacity-0 sm:group-hover:opacity-100">
              <Maximize2 size={13} /> Perbesar
            </span>
          </div>
        )}
      </div>

      {hasMultiple && (
        <div className="mt-2 flex gap-2 overflow-x-auto pb-1 sm:mt-3 sm:gap-3" role="list" aria-label="Pilih gambar galeri">
          {gallery.map((image, index) => (
            <button
              key={image.id}
              type="button"
              onClick={() => setActiveIndex(index)}
              className={`relative h-16 w-24 shrink-0 overflow-hidden rounded-xl border-2 transition sm:h-20 sm:w-28 ${
                activeIndex === index
                  ? 'border-[#7B2FBE] shadow-md shadow-purple-500/20'
                  : 'border-transparent opacity-65 hover:opacity-100 dark:border-white/5'
              }`}
              aria-label={`Tampilkan gambar ${index + 1}`}
              aria-current={activeIndex === index ? 'true' : undefined}
              role="listitem"
            >
              <FacilityImage item={image} alt="" sizes="112px" className="h-full w-full" />
              {image.isCover && <span className="absolute bottom-1 left-1 rounded bg-black/60 px-1.5 py-0.5 text-[9px] font-semibold text-white">Cover</span>}
            </button>
          ))}
        </div>
      )}

      {typeof document !== 'undefined' && createPortal(<AnimatePresence>
        {lightboxOpen && activeImage && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            role="dialog"
            aria-modal="true"
            aria-label={`Gambar ${activeIndex + 1} dari ${gallery.length}`}
            className="fixed inset-0 z-[100] flex items-center justify-center bg-black/95 p-3 backdrop-blur-md sm:p-8"
            onClick={() => setLightboxOpen(false)}
          >
            <button type="button" onClick={() => setLightboxOpen(false)} className="absolute right-4 top-4 z-10 grid h-11 w-11 place-items-center rounded-full bg-white/10 text-white transition hover:bg-white/20" aria-label="Tutup galeri">
              <X size={22} />
            </button>
            {hasMultiple && (
              <>
                <button type="button" onClick={(event) => { event.stopPropagation(); previousImage(); }} className="absolute left-3 z-10 grid h-11 w-11 place-items-center rounded-full bg-white/10 text-white transition hover:bg-white/20 sm:left-6 sm:h-12 sm:w-12" aria-label="Gambar sebelumnya">
                  <ChevronLeft size={26} />
                </button>
                <button type="button" onClick={(event) => { event.stopPropagation(); nextImage(); }} className="absolute right-3 z-10 grid h-11 w-11 place-items-center rounded-full bg-white/10 text-white transition hover:bg-white/20 sm:right-6 sm:h-12 sm:w-12" aria-label="Gambar berikutnya">
                  <ChevronRight size={26} />
                </button>
              </>
            )}
            <motion.div
              key={activeImage.imageUrl}
              initial={{ opacity: 0, scale: 0.97 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.97 }}
              transition={{ duration: 0.2 }}
              className="flex max-h-full max-w-[92vw] flex-col items-center"
              onClick={(event) => event.stopPropagation()}
            >
              <img src={activeImage.imageUrl} alt={`${item.name} - gambar ${activeIndex + 1}`} className="max-h-[82vh] max-w-full rounded-xl object-contain shadow-2xl" />
              <p className="mt-4 text-sm font-medium text-white/75">{item.name} · {activeIndex + 1} dari {gallery.length}</p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>, document.body)}
    </motion.section>
  );
}

function LoadingState() {
  return (
    <div className="grid min-h-[70vh] place-items-center bg-[#F8F7FF] pt-[70px] dark:bg-[#121020]">
      <div className="text-center">
        <Loader2 className="mx-auto animate-spin text-[#7B2FBE]" size={34} />
        <p className="mt-3 text-sm text-gray-500 dark:text-gray-400">Memuat detail fasilitas…</p>
      </div>
    </div>
  );
}

function NotFoundState() {
  return (
    <div className="grid min-h-[75vh] place-items-center bg-[#F8F7FF] px-4 pt-[70px] dark:bg-[#121020]">
      <div className="max-w-md text-center">
        <div className="mx-auto grid h-16 w-16 place-items-center rounded-2xl bg-purple-100 text-[#7B2FBE] dark:bg-purple-900/30">
          <Building2 size={30} />
        </div>
        <h1 className="mt-5 text-2xl font-bold text-gray-900 dark:text-white">Fasilitas tidak ditemukan</h1>
        <p className="mt-2 text-sm leading-6 text-gray-500 dark:text-gray-400">Halaman mungkin telah dipindahkan atau belum dipublikasikan.</p>
        <Link to="/sarana-prasarana" className="mt-6 inline-flex items-center gap-2 rounded-xl bg-[#7B2FBE] px-5 py-3 text-sm font-semibold text-white shadow-lg shadow-purple-500/20 transition hover:-translate-y-0.5 hover:bg-[#6924a7]">
          <ArrowLeft size={16} /> Kembali ke daftar fasilitas
        </Link>
      </div>
    </div>
  );
}

export default function SaranaPrasaranaDetail() {
  const { slug } = useParams();
  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setNotFound(false);
    fetchSaranaPrasaranaBySlug(slug)
      .then((data) => {
        if (!alive) return;
        if (!data) setNotFound(true);
        else setItem(data);
      })
      .catch(() => {
        if (alive) setNotFound(true);
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => { alive = false; };
  }, [slug]);

  if (loading) return <LoadingState />;
  if (notFound || !item) return <NotFoundState />;

  const details = item.details || [];
  const related = item.related || [];

  return (
    <div className="min-h-screen bg-[#F8F7FF] pt-[70px] dark:bg-[#121020]">
      <SEO title={item.name} description={item.deskripsi} image={imageVariant(item.imageUrl, 1200)} />

      <section className="page-hero relative overflow-hidden">
        <div className="absolute inset-0 opacity-10 [background-image:radial-gradient(circle_at_75%_25%,white_0,transparent_35%)]" />
        <div className="relative z-10 mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8 lg:py-16">
          <nav className="flex items-center gap-2 overflow-hidden text-sm text-white/65" aria-label="Breadcrumb">
            <Link to="/" className="flex shrink-0 items-center gap-1 transition hover:text-white"><Home size={14} /> Beranda</Link>
            <ChevronRight size={14} className="shrink-0" />
            <Link to="/sarana-prasarana" className="shrink-0 transition hover:text-white">Sarana & Prasarana</Link>
            <ChevronRight size={14} className="shrink-0" />
            <span className="truncate text-white">{item.name}</span>
          </nav>

          <motion.div initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="mt-8 max-w-4xl">
            <span className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-3 py-1.5 text-xs font-semibold text-white backdrop-blur-sm">
              <Building2 size={14} /> {item.category?.label || 'Fasilitas Kampus'}
            </span>
            <h1 className="mt-4 font-heading text-3xl font-bold leading-tight text-white sm:text-4xl lg:text-5xl">{item.name}</h1>
            <p className="mt-4 max-w-3xl text-sm leading-7 text-white/75 sm:text-base">{item.deskripsi}</p>
          </motion.div>
        </div>
      </section>

      <main className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8 lg:py-14">
        <Link to="/sarana-prasarana" className="mb-6 inline-flex items-center gap-2 text-sm font-semibold text-[#7B2FBE] transition hover:-translate-x-0.5 hover:text-[#64239d] dark:text-purple-300">
          <ArrowLeft size={16} /> Semua sarana & prasarana
        </Link>

        <div className="grid gap-7 lg:grid-cols-[minmax(0,1fr)_320px]">
          <div className="space-y-7">
            <FacilityGallery item={item} />

            <section className="rounded-3xl border border-gray-100 bg-white p-6 shadow-sm dark:border-white/10 dark:bg-[#1A182E] sm:p-8">
              <div className="flex items-center gap-3">
                <div className="grid h-11 w-11 place-items-center rounded-2xl bg-purple-100 text-[#7B2FBE] dark:bg-purple-900/30 dark:text-purple-300"><Sparkles size={21} /></div>
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#7B2FBE] dark:text-purple-300">Tentang fasilitas</p>
                  <h2 className="mt-0.5 text-xl font-bold text-gray-900 dark:text-white">Lingkungan yang mendukung kegiatan kampus</h2>
                </div>
              </div>
              <p className="mt-5 whitespace-pre-line text-[15px] leading-8 text-gray-600 dark:text-gray-300">{item.deskripsi}</p>
            </section>

            <section className="rounded-3xl border border-gray-100 bg-white p-6 shadow-sm dark:border-white/10 dark:bg-[#1A182E] sm:p-8">
              <div className="flex items-center justify-between gap-4">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#7B2FBE] dark:text-purple-300">Informasi utama</p>
                  <h2 className="mt-1 text-xl font-bold text-gray-900 dark:text-white">Fasilitas yang tersedia</h2>
                </div>
                <span className="rounded-full bg-purple-50 px-3 py-1 text-xs font-semibold text-[#7B2FBE] dark:bg-purple-900/30 dark:text-purple-300">{details.length} poin</span>
              </div>
              {details.length ? (
                <div className="mt-6 grid gap-3 sm:grid-cols-2">
                  {details.map((detail, index) => (
                    <motion.div key={detail} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.04 }} className="flex items-start gap-3 rounded-2xl border border-gray-100 bg-gray-50/70 p-4 dark:border-white/10 dark:bg-white/[0.03]">
                      <CheckCircle2 size={19} className="mt-0.5 shrink-0 text-[#7B2FBE] dark:text-purple-300" />
                      <span className="text-sm leading-6 text-gray-700 dark:text-gray-300">{detail}</span>
                    </motion.div>
                  ))}
                </div>
              ) : (
                <p className="mt-5 text-sm text-gray-500 dark:text-gray-400">Informasi rinci fasilitas akan segera dilengkapi.</p>
              )}
            </section>
          </div>

          <aside className="lg:sticky lg:top-24 lg:self-start">
            <div className="overflow-hidden rounded-3xl border border-gray-100 bg-white shadow-lg shadow-purple-950/5 dark:border-white/10 dark:bg-[#1A182E]">
              <div className="bg-gradient-to-br from-[#7B2FBE] to-[#512078] p-6 text-white">
                <div className="grid h-12 w-12 place-items-center rounded-2xl bg-white/15 backdrop-blur"><Layers3 size={23} /></div>
                <p className="mt-5 text-xs font-semibold uppercase tracking-[0.18em] text-white/65">Kategori</p>
                <h2 className="mt-1 text-lg font-bold">{item.category?.label || 'Fasilitas Kampus'}</h2>
              </div>
              <div className="space-y-4 p-6">
                <div className="flex items-center justify-between border-b border-gray-100 pb-4 text-sm dark:border-white/10">
                  <span className="text-gray-500 dark:text-gray-400">Status</span>
                  <span className="inline-flex items-center gap-1.5 font-semibold text-emerald-600 dark:text-emerald-400"><span className="h-2 w-2 rounded-full bg-emerald-500" /> Tersedia</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-500 dark:text-gray-400">Informasi</span>
                  <span className="font-semibold text-gray-800 dark:text-white">{details.length} poin detail</span>
                </div>
                <Link to="/sarana-prasarana" className="mt-3 flex w-full items-center justify-center gap-2 rounded-xl bg-[#7B2FBE] px-4 py-3 text-sm font-semibold text-white shadow-lg shadow-purple-500/20 transition hover:-translate-y-0.5 hover:bg-[#6924a7]">
                  Jelajahi fasilitas lain <ArrowUpRight size={16} />
                </Link>
              </div>
            </div>
          </aside>
        </div>

        {related.length > 0 && (
          <section className="mt-14 border-t border-gray-200 pt-10 dark:border-white/10">
            <div className="flex flex-wrap items-end justify-between gap-4">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#7B2FBE] dark:text-purple-300">Masih satu kategori</p>
                <h2 className="mt-1 text-2xl font-bold text-gray-900 dark:text-white">Fasilitas terkait</h2>
              </div>
              <Link to="/sarana-prasarana" className="text-sm font-semibold text-[#7B2FBE] dark:text-purple-300">Lihat semua</Link>
            </div>
            <div className="mt-6 grid gap-5 md:grid-cols-2 lg:grid-cols-3">
              {related.map((entry) => (
                <Link key={entry.id} to={`/sarana-prasarana/${entry.slug}`} className="group overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-sm transition hover:-translate-y-1 hover:shadow-xl dark:border-white/10 dark:bg-[#1A182E]">
                  <FacilityImage item={entry} className="aspect-[16/9] w-full transition duration-500 group-hover:scale-105" />
                  <div className="p-5">
                    <h3 className="font-bold text-gray-900 dark:text-white">{entry.name}</h3>
                    <p className="mt-2 line-clamp-2 text-sm leading-6 text-gray-500 dark:text-gray-400">{entry.deskripsi}</p>
                    <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-[#7B2FBE] dark:text-purple-300">Lihat detail <ArrowUpRight size={15} /></span>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}
      </main>
    </div>
  );
}
